import React, { useEffect, useMemo, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate } from "react-router-dom";
import { Clock } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState } from "@/components/ListState";
import { formatNlDate, formatEuro } from "@/components/Badges";
import { formatUren, parseUren, UURTYPE_LABELS } from "@/lib/uren";
import { Code } from "@/components/Code";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);
const weekStart = () => {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // maandag = 0
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
};

// §1: intern-hours categories. "Overig" is deliberately last — an unnamed
// bucket is fine as long as the ordinary options come first.
const INTERN_LABELS = {
  administratie: "Administratie",
  voorbereiding: "Voorbereiding",
  onderhoud: "Onderhoud",
  renovatie: "Renovatie",
  overig: "Overig",
};

const EMPTY = { kind: "werkbon", werkbon_id: "", intern_soort: "administratie", datum: "", uren: "", uurtype: "normaal", note: "", user_id: "" };
const CHIPS = ["0,5", "1", "2", "4", "8"];

// The screen that decides whether this round works: two men, iPhones, wash
// bays, wet gloves. The day total is the whole answer to "half the time never
// lands on a bon".
export default function UrenPage() {
  const navigate = useNavigate();
  const { user, canWrite } = useAuth();
  const [lines, setLines] = useState([]);
  const [bonnen, setBonnen] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [tarieven, setTarieven] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [bonSearch, setBonSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [u, b, asg, t] = await Promise.all([
        api.get(`/uren?van=${weekStart()}&tot=${today()}&user_id=${user?.id || ""}`),
        api.get("/werkbonnen"),
        api.get("/users/assignees"),
        api.get("/settings/tarieven").catch(() => ({ data: null })),
      ]);
      setLines(u.data); setBonnen(b.data); setAssignees(asg.data); setTarieven(t.data);
    } catch (e) { setError(getErrorMessage(e, "Kan uren niet laden.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const todayLines = useMemo(() => lines.filter((l) => l.datum === today()), [lines]);
  const todayTotal = todayLines.reduce((n, l) => n + (l.uren || 0), 0);
  // §1: split into doorbelast (on a bon) and eigen (intern). The two numbers
  // together are the whole day — that is the measurement the owner missed.
  const todayDoorbelast = todayLines.filter((l) => l.werkbon_id).reduce((n, l) => n + (l.uren || 0), 0);
  const todayEigen = todayLines.filter((l) => !l.werkbon_id).reduce((n, l) => n + (l.uren || 0), 0);
  const weekTotal = lines.reduce((n, l) => n + (l.uren || 0), 0);
  const weekDoorbelast = lines.filter((l) => l.werkbon_id).reduce((n, l) => n + (l.uren || 0), 0);
  const weekEigen = lines.filter((l) => !l.werkbon_id).reduce((n, l) => n + (l.uren || 0), 0);
  const earlier = useMemo(() => lines.filter((l) => l.datum !== today()), [lines]);

  // Open bonnen first, then the rest, both newest first — recent work on top.
  const pickList = useMemo(() => {
    const q = bonSearch.trim().toLowerCase();
    const rows = bonnen
      .filter((b) => b.status !== "geannuleerd")
      .filter((b) => !q || [b.werkbon_number, b.omschrijving, b.location_name].filter(Boolean).join(" ").toLowerCase().includes(q));
    const open = rows.filter((b) => ["voorbereid", "uitgegeven"].includes(b.status));
    const rest = rows.filter((b) => !["voorbereid", "uitgegeven"].includes(b.status));
    return [...open, ...rest].slice(0, 12);
  }, [bonnen, bonSearch]);

  const chosenBon = bonnen.find((b) => b.id === form.werkbon_id);

  const openBoeken = (line) => {
    if (line) {
      setEditId(line.id);
      setForm({
        kind: line.werkbon_id ? "werkbon" : "intern",
        werkbon_id: line.werkbon_id || "",
        intern_soort: line.intern_soort || "administratie",
        datum: line.datum, uren: formatUren(line.uren), uurtype: line.uurtype,
        note: line.note || "", user_id: line.user_id,
      });
    } else {
      setEditId(null);
      setForm({ ...EMPTY, datum: today(), user_id: user?.id || "" });
    }
    setBonSearch("");
    setDlg(true);
  };

  const save = async () => {
    const { value, error: uErr } = parseUren(form.uren);
    if (uErr || value === null) { toast.error(uErr || "Vul het aantal uren in."); return; }
    if (!editId) {
      if (form.kind === "werkbon" && !form.werkbon_id) { toast.error("Kies eerst de werkbon."); return; }
      if (form.kind === "intern" && !form.intern_soort) { toast.error("Kies om welke eigen tijd het gaat."); return; }
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/uren/${editId}`, { datum: form.datum, uren: value, uurtype: form.uurtype, note: form.note || null });
      } else {
        await api.post("/uren", {
          werkbon_id: form.kind === "werkbon" ? form.werkbon_id : null,
          intern_soort: form.kind === "intern" ? form.intern_soort : null,
          datum: form.datum, uren: value, uurtype: form.uurtype,
          note: form.note || null, user_id: form.user_id || null,
        });
      }
      const bonLabel = form.kind === "werkbon" && chosenBon ? ` op ${chosenBon.werkbon_number}` : (form.kind === "intern" ? ` (${INTERN_LABELS[form.intern_soort]})` : "");
      toast.success(`Geboekt: ${formatUren(value)} uur${bonLabel}`);
      setDlg(false);
      await load();
    } catch (e) {
      // The typed line is not lost: the dialog stays open with everything in it.
      toast.error(getErrorMessage(e, `Niet opgeslagen: "${formatUren(value)} uur"`));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/uren/${editId}`);
      toast.success("Urenregel verwijderd");
      setDlg(false);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Verwijderen mislukt.")); }
    finally { setSaving(false); }
  };

  const facts = loading ? ["laden…"] : [
    `vandaag: ${formatUren(todayDoorbelast)} doorbelast + ${formatUren(todayEigen)} eigen`,
    `deze week ${formatUren(weekTotal)} uur (${formatUren(weekDoorbelast)} + ${formatUren(weekEigen)})`,
  ];
  const actions = canWrite()
    ? [{ key: "boeken", label: "Uren boeken", icon: Clock, variant: "primary", testid: "uren-boeken-button", onClick: () => openBoeken(null) }]
    : [];

  // td reads the rate too: they should know what their hour is worth.
  const rateLine = useMemo(() => {
    if (!tarieven) return null;
    const parts = (tarieven.uurtypes || []).map((u) => {
      const r = tarieven.actueel?.[u.key];
      return r != null ? `${u.label} ${formatEuro(r)}` : null;
    }).filter(Boolean);
    return parts.length ? parts.join(" · ") : "tarief nog niet ingesteld";
  }, [tarieven]);

  const Line = ({ l }) => (
    <button type="button" data-testid={`uren-regel-${l.id}`} className="block w-full py-3 text-left transition-colors hover:bg-accent" onClick={() => canWrite() && openBoeken(l)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-baseline gap-3">
          <span className="font-mono tabular-nums text-body font-medium text-foreground">{formatUren(l.uren)} uur</span>
          {l.werkbon_id ? (
            <Code size="small">{l.werkbon_number}</Code>
          ) : (
            <span className="text-data uppercase tracking-[0.07em] text-inkt-zacht" data-testid={`uren-regel-intern-${l.id}`}>
              intern · {INTERN_LABELS[l.intern_soort] || l.intern_soort}
            </span>
          )}
        </div>
        <span className="text-data text-muted-foreground">{UURTYPE_LABELS[l.uurtype]}</span>
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-3 text-data text-muted-foreground">
        {l.werkbon_id && <span>{l.werkbon_omschrijving}</span>}
        {l.note && <span>— {l.note}</span>}
      </div>
    </button>
  );

  return (
    <Page console={{ variant: "view", title: "Uren", facts, actions }}>
      <div className="space-y-4">
        {error && !loading ? (
          <ErrorState testid="uren-load-error" message={error} onRetry={load} />
        ) : loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <Card key={i} className="p-4"><Skeleton className="h-12 w-full" /></Card>)}
          </div>
        ) : (
          <>
            <Card className="p-5" data-testid="uren-vandaag">
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h2 className="text-body font-semibold text-foreground">Vandaag</h2>
                <span className="font-mono tabular-nums text-kop text-foreground" data-testid="uren-vandaag-totaal">{formatUren(todayTotal)} uur</span>
              </div>
              <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-data text-muted-foreground">
                <span data-testid="uren-vandaag-doorbelast">Doorbelast: <span className="font-mono tabular-nums text-foreground">{formatUren(todayDoorbelast)}</span></span>
                <span data-testid="uren-vandaag-eigen">Eigen tijd: <span className="font-mono tabular-nums text-foreground">{formatUren(todayEigen)}</span></span>
              </div>
              {todayLines.length === 0 ? (
                <p className="py-3 text-body text-muted-foreground" data-testid="uren-vandaag-leeg">Nog niets geboekt vandaag. Elk uur dat hier niet staat, verdampt.</p>
              ) : (
                <div className="divide-y divide-border">{todayLines.map((l) => <Line key={l.id} l={l} />)}</div>
              )}
            </Card>

            {earlier.length > 0 && (
              <Card className="p-5" data-testid="uren-week">
                <h2 className="mb-2 text-body font-semibold text-foreground">Eerder deze week</h2>
                <div className="divide-y divide-border">
                  {earlier.map((l) => (
                    <div key={l.id}>
                      <div className="pt-2 text-label uppercase tracking-[0.07em] text-muted-foreground">{formatNlDate(l.datum)}</div>
                      <Line l={l} />
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {rateLine && (
              <p className="text-data text-muted-foreground" data-testid="uren-tarief-regel">Uurtarief: {rateLine}</p>
            )}
          </>
        )}

        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogContent srDescription="Boek uren: kies de werkbon, de datum en het aantal uren.">
            <DialogHeader><DialogTitle>{editId ? "Urenregel corrigeren" : "Uren boeken"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 gap-3">
              {!editId && (
                <>
                  {/* §1: werkbon vs intern. The two options next to each other,
                      the werkbon one selected by default because that is the
                      primary case; intern is the smaller, honest bucket. */}
                  <div className="flex gap-2" data-testid="uren-kind-toggle">
                    <Button
                      type="button"
                      variant={form.kind === "werkbon" ? "default" : "outline"}
                      size="sm"
                      data-testid="uren-kind-werkbon"
                      onClick={() => setForm((f) => ({ ...f, kind: "werkbon" }))}
                    >
                      Werkbon
                    </Button>
                    <Button
                      type="button"
                      variant={form.kind === "intern" ? "default" : "outline"}
                      size="sm"
                      data-testid="uren-kind-intern"
                      onClick={() => setForm((f) => ({ ...f, kind: "intern" }))}
                    >
                      Eigen tijd (intern)
                    </Button>
                  </div>
                  {form.kind === "werkbon" && (
                    <div className="space-y-1.5">
                      <Label>Werkbon *</Label>
                      {chosenBon ? (
                        <button type="button" data-testid="uren-bon-chosen" className="flex w-full items-center justify-between gap-2 rounded border border-veld-rand px-3 py-2 text-left" onClick={() => setForm((f) => ({ ...f, werkbon_id: "" }))}>
                          <span className="min-w-0 truncate text-body text-foreground"><Code size="small">{chosenBon.werkbon_number}</Code> {chosenBon.omschrijving}</span>
                          <span className="shrink-0 text-data text-primary">wijzig</span>
                        </button>
                      ) : (
                        <>
                          <Input data-testid="uren-bon-search" placeholder="Zoek op nummer of omschrijving" value={bonSearch} onChange={(e) => setBonSearch(e.target.value)} />
                          <div className="max-h-48 divide-y divide-border overflow-y-auto rounded border border-lijn-zacht" data-testid="uren-bon-list">
                            {pickList.length === 0 ? (
                              <p className="px-3 py-4 text-body text-muted-foreground">Geen werkbon gevonden.</p>
                            ) : pickList.map((b) => (
                              <button key={b.id} type="button" data-testid={`uren-bon-pick-${b.id}`} className="block w-full px-3 py-2.5 text-left transition-colors hover:bg-accent" onClick={() => setForm((f) => ({ ...f, werkbon_id: b.id }))}>
                                <div className="flex items-center justify-between gap-2">
                                  <Code size="small">{b.werkbon_number}</Code>
                                  <span className="text-data text-muted-foreground">{b.location_name}</span>
                                </div>
                                <div className="truncate text-body text-foreground">{b.omschrijving}</div>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {form.kind === "intern" && (
                    <div className="space-y-1.5">
                      <Label>Eigen tijd — welke soort *</Label>
                      <Select value={form.intern_soort} onValueChange={(v) => setForm((f) => ({ ...f, intern_soort: v }))}>
                        <SelectTrigger data-testid="uren-intern-soort-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(INTERN_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-data text-muted-foreground">
                        Deze uren draaien op Techniek &amp; Beheer BV zelf — niet doorbelast, wel geboekt.
                      </p>
                    </div>
                  )}
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Datum</Label><Input type="date" data-testid="uren-datum-input" value={form.datum} onChange={(e) => setForm((f) => ({ ...f, datum: e.target.value }))} /></div>
                <div className="space-y-1.5">
                  <Label>Uren</Label>
                  <Input inputMode="decimal" placeholder="1,5" data-testid="uren-uren-input" value={form.uren} onChange={(e) => setForm((f) => ({ ...f, uren: e.target.value }))} />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {CHIPS.map((c) => (
                  <Button key={c} type="button" variant="outline" size="sm" data-testid={`uren-chip-${c.replace(",", "-")}`} onClick={() => setForm((f) => ({ ...f, uren: c }))}>{c}</Button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Uurtype</Label>
                  <Select value={form.uurtype} onValueChange={(v) => setForm((f) => ({ ...f, uurtype: v }))}>
                    <SelectTrigger data-testid="uren-uurtype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(UURTYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {!editId && (
                  <div className="space-y-1.5">
                    <Label>Voor</Label>
                    <Select value={form.user_id} onValueChange={(v) => setForm((f) => ({ ...f, user_id: v }))}>
                      <SelectTrigger data-testid="uren-voor-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{assignees.map((u) => <SelectItem key={u.id} value={u.id}>{u.id === user?.id ? `${u.name} (ik)` : u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="space-y-1.5"><Label>Notitie (optioneel)</Label><Input data-testid="uren-note-input" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
            </div>
            <DialogFooter className="gap-2">
              {editId && (
                <Button variant="outline" data-testid="uren-delete-button" onClick={remove} disabled={saving}>Verwijderen</Button>
              )}
              <Button variant="secondary" onClick={() => setDlg(false)}>Terug</Button>
              <Button data-testid="uren-save-button" onClick={save} disabled={saving}>{saving ? "Bezig..." : "Boeken"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Page>
  );
}
